/**
 * 8G.2D.2D.1 — THE ANALYSIS INPUT FOR OPTIPLACE, BUILT EXPLICITLY.
 *
 * ── WHY THIS FILE EXISTS ───────────────────────────────────────────────────
 *
 * 8G.2D.2D wired persisted OptiPlace to `execute_structural_split_atomic` and
 * proved the authority end to end. It did NOT prove the product could reach
 * that call, and independent review was right: it could not.
 *
 * Measured on the branch, each link:
 *
 *   1. `OptiboardTicketRow` passes `jobs: []` for a persisted ticket.
 *   2. `OptiPlaceModal` hydrates canonical `ticket_jobs` — asynchronously.
 *   3. `analyzeAssignmentsForDate` sends `{ ...ticketData }` to smart-assign,
 *      so it sends that empty array; the hydrated lines are never substituted.
 *   4. smart-assign gates split analysis on
 *      `ticketData.jobs && ticketData.jobs.length >= 2` (index.ts:580).
 *   5. smart-assign never hydrates the request's OWN source lines. Its one
 *      earlier `ticket_jobs` read (index.ts:505) fetches OTHER active tickets
 *      for board context.
 *   6. `handlePreviewSplit` returns immediately unless
 *      `splitSuggestion.eligible`.
 *
 * So: zero jobs in, gate never opens, no suggestion, Preview Split unreachable,
 * and the canonical confirm path is dead code from the Optiboard entry.
 *
 * The analysis payload was being produced by spreading `ticketData` and hoping
 * its fields were right. It is built here instead, where it can be tested.
 *
 * ── THE IDENTITY RULE ──────────────────────────────────────────────────────
 *
 * For a PERSISTED source the analysis jobs come STRAIGHT from the database
 * rows, and each `id` is a real `ticket_jobs.job_id`. Nothing is correlated
 * back to `ticketData.jobs` by name, array position, category, minutes,
 * `catalog_job_id` or `tempJobId`. The DB row is the identity — the same
 * identity that later reaches the structural authority, so what the operator
 * was shown and what gets moved cannot drift apart.
 *
 * For an UNPERSISTED ticket there is no source and nothing to move, so the
 * existing `ticketData.jobs` behaviour is preserved untouched.
 */

/** A canonical source job line, exactly as read from `ticket_jobs`. */
export interface CanonicalSourceJobLine {
  job_id: string;
  job_status: string | null;
  minutes_override: number | null;
  name: string;
  category: string;
  submenu: string | null;
  skill_level: number | null;
  bay_requirement: string | null;
  standard_minutes: number | null;
  proficiency_category: string | null;
}

/** The shape smart-assign and generateSplitOptions both consume. */
export interface AnalysisJob {
  id: string;
  name: string;
  category: string;
  submenu: string;
  skill: number;
  minutes: number;
  bay: string;
  proficiency_category: string;
}

/**
 * Hydration is a THREE-state fact, not a boolean.
 *
 * `ready` with zero lines is a real business state — a source that genuinely
 * has no job lines. `error` is not that, and neither is `loading`. Collapsing
 * them is how a failed read becomes "this ticket has no work", which is
 * exactly the shape of the defect this file repairs.
 */
export type HydrationState = 'loading' | 'ready' | 'error';

export interface AnalysisInputRequest {
  /** The ticket data the caller handed the modal. */
  ticketData: { id?: string | null; jobs?: Array<Record<string, unknown>> | null };
  /** Canonical DB rows; null until hydration resolves. */
  sourceJobLines: CanonicalSourceJobLine[] | null;
  /**
   * 8G.2D.2D.2 §2 — WHICH ticket those rows were read for.
   *
   * Hydration state alone answers "how far did the read get", never "whose
   * rows are these". The modal's effect re-queries on an id change but does
   * not clear the previous rows, so between `ticketData.id` becoming B and B's
   * rows arriving the component holds A's lines at `ready` — a persisted
   * proposal naming ticket A's work against ticket B. A late A response can
   * also land after B's and overwrite it.
   *
   * Binding the rows to their source makes that unrepresentable rather than
   * merely unlikely: no timing assumption can satisfy this check.
   */
  sourceJobLinesTicketId: string | null;
  /** Where hydration has actually got to. */
  hydration: HydrationState;
  /** Category → skill tier, injected so this stays pure and testable. */
  categorySkillTier: (category: string) => number;
}

export interface AnalysisInputResult {
  /** True when this is a persisted source (structural split territory). */
  persisted: boolean;
  /**
   * Whether split analysis may run AT ALL yet.
   *
   * False while a persisted source is still hydrating, and false if hydration
   * failed. A persisted structural split must never be decided from job data
   * that simply had not arrived.
   */
  splitAnalysisReady: boolean;
  /** The jobs to send. Empty while not ready — deliberately, and reported as such. */
  jobs: AnalysisJob[];
  /** Why analysis is not ready, when it is not. */
  blockedReason: 'HYDRATION_PENDING' | 'HYDRATION_FAILED' | 'HYDRATION_STALE' | null;
}

/**
 * Build the analysis payload's `jobs`, and say whether split analysis may run.
 *
 * A caller that ignores `splitAnalysisReady` and sends `jobs` anyway sends an
 * empty array — which is precisely the pre-repair behaviour — so the flag is
 * the contract, not a hint.
 */
export function buildOptiPlaceAnalysisJobs(
  req: AnalysisInputRequest,
): AnalysisInputResult {
  const persisted = !!req.ticketData.id;

  // ── UNPERSISTED: nothing to move, nothing to hydrate ────────────────────
  if (!persisted) {
    const jobs = (req.ticketData.jobs ?? []).map((job: any, idx: number) => ({
      // A disposable client key. It only has to correlate planning objects
      // with rows this same flow is about to insert, and it can never reach a
      // structural act because there is no source to act on.
      id: `job-${idx}-${Math.random().toString(36).substr(2, 9)}`,
      name: job.name,
      category: job.category,
      submenu: job.submenu ?? job.category,
      skill: req.categorySkillTier(job.category),
      minutes: job.minutes ?? job.standard_minutes ?? job.standardMinutes ?? 0,
      bay: job.bay ?? job.bay_requirement ?? '',
      proficiency_category: job.proficiency_category ?? job.category,
    }));
    return { persisted: false, splitAnalysisReady: true, jobs, blockedReason: null };
  }

  // ── PERSISTED: the database supplies both the work and its identity ─────
  //
  // ERROR is checked FIRST, deliberately. A failed read also leaves
  // `sourceJobLines` null, so testing for null first reported every failure as
  // "still loading" — which is the same collapse of distinct states this
  // three-state contract exists to prevent, reappearing one layer down. Caught
  // by the test that asserts a failed read is not a pending one.
  if (req.hydration === 'error') {
    // A failed read is NOT a source with no work. Refusing here is what stops
    // "we could not load the lines" from being silently rendered as
    // "this ticket cannot be split".
    return {
      persisted: true,
      splitAnalysisReady: false,
      jobs: [],
      blockedReason: 'HYDRATION_FAILED',
    };
  }
  if (req.hydration === 'loading' || req.sourceJobLines === null) {
    return {
      persisted: true,
      splitAnalysisReady: false,
      jobs: [],
      blockedReason: 'HYDRATION_PENDING',
    };
  }

  // ══ §2 — THE ROWS MUST BELONG TO THE TICKET BEING ANALYSED ══════════════
  // Checked after `ready`, because a stale binding is a distinct thing from a
  // read that has not finished: the data is complete, it is simply somebody
  // else's. Treated as not-ready rather than filtered, since a partially
  // correct proposal is worse than none.
  if (req.sourceJobLinesTicketId !== req.ticketData.id) {
    return {
      persisted: true,
      splitAnalysisReady: false,
      jobs: [],
      blockedReason: 'HYDRATION_STALE',
    };
  }

  // `ready` with zero lines is truthful and allowed through: the gate in
  // smart-assign will decline to offer a split, which is the correct answer
  // for a ticket that really has no work.
  const jobs = req.sourceJobLines.map((line) => ({
    id: line.job_id,                                  // CANONICAL identity
    name: line.name,
    category: line.category,
    submenu: line.submenu ?? line.category,
    skill: req.categorySkillTier(line.category),
    minutes: line.minutes_override ?? line.standard_minutes ?? 0,
    bay: line.bay_requirement ?? '',
    proficiency_category: line.proficiency_category ?? line.category,
  }));

  return { persisted: true, splitAnalysisReady: true, jobs, blockedReason: null };
}

/**
 * 8G.2D.2D.1 §8 — the identity of ONE confirmed structural proposal.
 *
 * The previous key covered only source + job ids + tech ids. The operator also
 * confirms WHEN each part starts and how long it runs, and those values are
 * sent to the authority — so changing them changes the human decision, and the
 * key has to notice.
 *
 * Canonicalised deterministically: job ids are sorted within a part, because a
 * part holding {A,B} is the same decision as one holding {B,A}; part order is
 * NOT sorted, because part 1 is the sequence that inherits the vehicle's
 * physical placement and swapping the parts is a different act.
 */
export function buildSplitProposalKey(
  sourceTicketId: string,
  parts: Array<{
    job_ids: string[];
    tech_id: string | null;
    start_time?: string | null;
    predicted_minutes?: number | null;
    bay_id?: string | null;
  }>,
): string {
  return JSON.stringify({
    source: sourceTicketId,
    parts: parts.map((p) => ({
      job_ids: [...p.job_ids].sort(),
      tech_id: p.tech_id ?? null,
      start_time: p.start_time ?? null,
      predicted_minutes: p.predicted_minutes ?? null,
      bay_id: p.bay_id ?? null,
    })),
  });
}

/**
 * 8G.2D.2D.2 §6 — THE OPERATION ID MUST OUTLIVE THE BROWSER TAB.
 *
 * Every split surface held its operation id in a `useRef` (or an in-memory
 * `Map`) alongside `crypto.randomUUID()`. That survives a rerender and a
 * transport retry, and nothing else.
 *
 * MEASURED CONSEQUENCE: if the authority COMMITS and the client never receives
 * the result, a page refresh destroys the key. Retrying the same human decision
 * mints a NEW id, so the authority no longer recognises the operation — it sees
 * a fresh attempt against a source that is already superseded and answers
 * `SOURCE_NOT_SPLITTABLE`. No second family is created, so nothing is
 * corrupted; but the operator is told their ticket cannot be split, when in
 * fact their split is the reason it cannot.
 *
 * The fix is to stop STORING the identity and start DERIVING it. The operation
 * id is a pure function of the canonical proposal, so the same human decision
 * yields the same id on any machine, in any tab, after any refresh — and the
 * authority answers ALREADY_APPLIED with the family it created.
 *
 * A materially different proposal hashes differently and is never collapsed
 * into the prior operation, which is the same property the content key already
 * had; this simply makes it durable.
 *
 * Determinism is safe here because a persisted source can only be split once:
 * after the first success it is superseded, so "the same proposal twice" is not
 * a state the product can reach with a different intent behind it.
 */
export async function deriveSplitOperationId(
  sourceTicketId: string,
  parts: Array<{
    job_ids: string[];
    tech_id: string | null;
    start_time?: string | null;
    predicted_minutes?: number | null;
    bay_id?: string | null;
  }>,
): Promise<string> {
  return deriveOperationIdFromKey(buildSplitProposalKey(sourceTicketId, parts));
}

/**
 * 8G.2D.3 §12 — the shared derivation, so every structural act keys the same way.
 *
 * Extracted rather than duplicated: merge needs exactly this property and a
 * second copy would be a second thing to get wrong. SHA-256 over the canonical
 * key, truncated to 128 bits and shaped as a v4-looking UUID so it satisfies
 * every uuid validator on the path. The version/variant nibbles are cosmetic;
 * the entropy is the hash. Six of the 128 bits are spent on them, leaving 122
 * — a ~2^61 birthday bound, far beyond any number of proposals that can exist.
 */
export async function deriveOperationIdFromKey(key: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(key),
  );
  const hex = Array.from(new Uint8Array(digest).subarray(0, 16), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    '4' + hex.slice(13, 16),
    ((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16) + hex.slice(17, 20),
    hex.slice(20, 32),
  ].join('-');
}
